import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowUpRight, Check, X, Filter } from 'lucide-react';
import { adminApi } from '../../lib/api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';

export default function AdminWithdrawals() {
  const [withdrawals, setWithdrawals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [rejectModal, setRejectModal] = useState({ open: false, withdrawalId: null });
  const [rejectReason, setRejectReason] = useState('');

  useEffect(() => {
    fetchWithdrawals();
  }, [filter]);

  const fetchWithdrawals = async () => {
    setLoading(true);
    try {
      const response = await adminApi.getWithdrawals(filter === 'all' ? null : filter);
      setWithdrawals(response.data.withdrawals);
    } catch (error) {
      console.error('Error fetching withdrawals:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (withdrawalId) => {
    try {
      await adminApi.processWithdrawal(withdrawalId, { action: 'approve' });
      toast.success('Withdrawal approved');
      fetchWithdrawals();
    } catch (error) {
      toast.error('Failed to approve withdrawal');
    }
  };

  const handleReject = async () => {
    try {
      await adminApi.processWithdrawal(rejectModal.withdrawalId, { 
        action: 'reject', 
        reason: rejectReason 
      });
      toast.success('Withdrawal rejected');
      setRejectModal({ open: false, withdrawalId: null });
      setRejectReason('');
      fetchWithdrawals();
    } catch (error) {
      toast.error('Failed to reject withdrawal');
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-withdrawals">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-3 rounded-xl bg-blue-500/10">
            <ArrowUpRight className="w-6 h-6 text-blue-400" />
          </div>
          <div>
            <h1 className="text-2xl text-white font-medium">Withdrawal Management</h1>
            <p className="text-sm text-zinc-500">Process withdrawal requests</p>
          </div>
        </div>
      </div>

      {/* Filter */}
      <div className="flex items-center gap-4">
        <Filter className="w-4 h-4 text-zinc-500" />
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-[180px] bg-black/50 border-white/10">
            <SelectValue placeholder="Filter" />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-white/10">
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
            <SelectItem value="all">All</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Withdrawals Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="glass-card overflow-hidden"
      >
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-10 h-10 border-4 border-amber-500/20 border-t-amber-500 rounded-full animate-spin" />
          </div>
        ) : withdrawals.length === 0 ? (
          <div className="text-center py-12">
            <ArrowUpRight className="w-16 h-16 text-zinc-600 mx-auto mb-4" />
            <p className="text-zinc-400">No withdrawals found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Amount</th>
                  <th>Fee</th>
                  <th>Net</th>
                  <th>Wallet</th>
                  <th>Date</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {withdrawals.map((w) => (
                  <tr key={w.id}>
                    <td>
                      <div>
                        <p className="text-white font-medium">{w.user?.full_name}</p>
                        <p className="text-xs text-zinc-500">{w.user?.email}</p>
                      </div>
                    </td>
                    <td className="text-white font-medium">${w.amount.toFixed(2)}</td>
                    <td className="text-red-400">-${w.fee.toFixed(2)}</td>
                    <td className="text-green-400">${w.net_amount.toFixed(2)}</td>
                    <td className="text-zinc-400 font-mono text-xs">
                      {w.wallet_address.slice(0, 15)}...
                    </td>
                    <td className="text-zinc-400">
                      {new Date(w.created_at).toLocaleDateString()}
                    </td>
                    <td>
                      <span className={`badge ${
                        w.status === 'approved' ? 'badge-success' :
                        w.status === 'rejected' ? 'badge-danger' : 'badge-warning'
                      }`}>
                        {w.status}
                      </span>
                    </td>
                    <td>
                      {w.status === 'pending' ? (
                        <div className="flex gap-1">
                          <button
                            onClick={() => handleApprove(w.id)}
                            className="p-1.5 rounded bg-green-500/20 text-green-400 hover:bg-green-500/30"
                            title="Approve"
                            data-testid={`approve-withdrawal-${w.id}`}
                          >
                            <Check className="w-3 h-3" />
                          </button>
                          <button
                            onClick={() => setRejectModal({ open: true, withdrawalId: w.id })}
                            className="p-1.5 rounded bg-red-500/20 text-red-400 hover:bg-red-500/30"
                            title="Reject"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ) : (
                        <span className="text-xs text-zinc-500">Processed</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </motion.div>

      {/* Reject Modal */}
      <Dialog open={rejectModal.open} onOpenChange={(open) => setRejectModal({ ...rejectModal, open })}>
        <DialogContent className="bg-zinc-900 border-white/10">
          <DialogHeader>
            <DialogTitle className="text-white">Reject Withdrawal</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <p className="text-sm text-zinc-400">
              The amount will be refunded to the user's balance.
            </p>
            <div>
              <label className="text-xs text-zinc-400 mb-2 block">Rejection Reason</label>
              <Input
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Enter reason for rejection"
                className="bg-black/50 border-white/10"
              />
            </div>
            <div className="flex gap-3">
              <Button onClick={handleReject} className="flex-1 bg-red-500 hover:bg-red-600">
                Reject & Refund
              </Button>
              <Button 
                variant="outline" 
                onClick={() => setRejectModal({ open: false, withdrawalId: null })}
                className="flex-1 border-white/10"
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
